import { isTouchDevice, isMobile } from "common/utils/browser";
import "./quantity-price-popover.scss";

class QuantityPricePopover {
  #SELECTORS = {
    QTY_PRICING_POPOVER: ".js-qty-pricing-popover",
    QTY_PRICING_POPOVER_CONTENT: ".js-qty-pricing-popover-content",
    QTY_PRICING_POPOVER_PORTAL: ".quantity-price-popover__portal-container"
  };

  #$productCard;

  #$portal;

  #isOpen = false;

  #closeTimer;

  #scrollHandler;

  #resizeHandler;

  get #$qtyPricingPopover() {
    return this.#$productCard.querySelector(this.#SELECTORS.QTY_PRICING_POPOVER);
  }

  get #$popoverContent() {
    return this.#$qtyPricingPopover.querySelector(this.#SELECTORS.QTY_PRICING_POPOVER_CONTENT);
  }

  get #$popoverPortal() {
    return document.querySelectorAll(this.#SELECTORS.QTY_PRICING_POPOVER_PORTAL);
  }

  #createPortal() {
    const $content = this.#$popoverContent.cloneNode(true);
    $content.classList.remove("hidden");
    $content.classList.add("quantity-price-popover__portal");

    const $portal = document.createElement("div");
    $portal.className = "quantity-price-popover__portal-container";
    $portal.appendChild($content);

    return $portal;
  }

  #positionPortal() {
    if (!this.#$portal) return;

    const rect = this.#$qtyPricingPopover.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    this.#$portal.style.left = `${rect.left + scrollLeft + rect.width / 2}px`;

    if (isMobile()) {
      this.#$portal.style.top = `${rect.bottom + scrollTop + 5}px`;
    } else {
      const popoverHeight = this.#$portal.offsetHeight;
      this.#$portal.style.top = `${rect.top + scrollTop - popoverHeight - 5}px`;
    }
  }

  #showPortal() {
    if (this.#$portal) return;

    this.#$popoverPortal.forEach(el => el.remove());

    this.#$portal = this.#createPortal();
    document.body.appendChild(this.#$portal);
    this.#positionPortal();

    this.#$portal.addEventListener("mouseenter", () => {
      clearTimeout(this.#closeTimer);
    });

    this.#$portal.addEventListener("mouseleave", () => {
      this.#closeTimer = setTimeout(() => {
        this.#toggleQtyPricingPopover();
      }, 100);
    });

    this.#scrollHandler = () => this.#positionPortal();
    this.#resizeHandler = () => this.#positionPortal();
    window.addEventListener("scroll", this.#scrollHandler, true);
    window.addEventListener("resize", this.#resizeHandler);

    this.#$productCard.classList.add("product-card--popover-open");
  }

  #hidePortal() {
    if (!this.#$portal) return;

    this.#$portal.remove();
    this.#$portal = null;

    if (this.#scrollHandler) {
      window.removeEventListener("scroll", this.#scrollHandler, true);
      this.#scrollHandler = null;
    }
    if (this.#resizeHandler) {
      window.removeEventListener("resize", this.#resizeHandler);
      this.#resizeHandler = null;
    }

    this.#$productCard.classList.remove("product-card--popover-open");
  }

  #toggleQtyPricingPopover(isKeyboardEvent = false) {
    if (!this.#$qtyPricingPopover) return;

    this.#isOpen = !this.#isOpen;
    this.#$qtyPricingPopover.setAttribute("aria-expanded", this.#isOpen);

    if (this.#isOpen) {
      this.#showPortal();
      if (isKeyboardEvent && this.#$portal) {
        const $content = this.#$popoverContent;
        $content.setAttribute("tabindex", "-1");
        $content.focus();
      }
    } else {
      this.#hidePortal();
    }
  }

  #handleTouchStart = e => {
    if (!this.#isOpen || !this.#$portal) return;

    const $popoverContainer = this.#$qtyPricingPopover;
    if (!$popoverContainer.contains(e.target) && !this.#$portal.contains(e.target)) {
      this.#toggleQtyPricingPopover();
    }
  };

  #handleKeyDown = e => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.#toggleQtyPricingPopover(true);
    }
  };

  #handleDocumentKeyDown = e => {
    if (e.key === "Escape" && this.#isOpen) {
      this.#toggleQtyPricingPopover(true);
    }
  };

  #handleClickOutside = e => {
    if (!this.#isOpen || !this.#$portal) return;

    const $popoverContainer = this.#$qtyPricingPopover;

    const clickedOnTrigger = $popoverContainer === e.target || $popoverContainer.contains(e.target);
    const clickedOnPortal = this.#$portal === e.target || this.#$portal.contains(e.target);

    if (!clickedOnTrigger && !clickedOnPortal) {
      this.#toggleQtyPricingPopover();
    }
  };

  init($productCard) {
    this.#$productCard = $productCard;

    if (!this.#$qtyPricingPopover) return;

    this.#$qtyPricingPopover.addEventListener("keydown", this.#handleKeyDown);

    if (isTouchDevice()) {
      this.#$qtyPricingPopover.addEventListener("click", () => {
        this.#toggleQtyPricingPopover();
      });
    } else {
      this.#$qtyPricingPopover.addEventListener("mouseenter", () => {
        clearTimeout(this.#closeTimer);
        if (!this.#isOpen) {
          this.#toggleQtyPricingPopover();
        }
      });

      this.#$qtyPricingPopover.addEventListener("mouseleave", () => {
        this.#closeTimer = setTimeout(() => {
          if (this.#isOpen) {
            this.#toggleQtyPricingPopover();
          }
        }, 100);
      });

      this.#$qtyPricingPopover.addEventListener("click", () => {
        this.#toggleQtyPricingPopover();
      });
    }

    document.addEventListener("touchstart", this.#handleTouchStart);
    document.addEventListener("keydown", this.#handleDocumentKeyDown);
    document.addEventListener("click", this.#handleClickOutside);
  }
}

export default $productCard => new QuantityPricePopover().init($productCard);
